import React, {Fragment, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect, useDispatch} from 'react-redux';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import {addMachine, clearErrors, getEmployeeOptions, getEmployeeOptionsByFirm} from "../../actions/machine";
import useActiveFirm from '../../hooks/useActiveFirm';
import {getParams} from "../../utils/hooks/getParams";

const MachineCreateForm = ({
                               machine: {firm_options, employee_options, error, loading, currentPage},
                               addMachine,
                               clearErrors,
                               handleClose,
                               show
                           }) => {
    const dispatch = useDispatch();
    const { firmId } = useActiveFirm();

    useEffect(() => {
        if (firmId) {
            dispatch(getEmployeeOptionsByFirm(firmId));
        } else {
            dispatch(getEmployeeOptions());
        }
    }, [dispatch, firmId]);

    // Auto-populate with global firm when modal opens
    useEffect(() => {
        if (show && firmId) {
            setInputs(prev => ({ ...prev, firm_id: firmId }));
            dispatch(getEmployeeOptionsByFirm(firmId));
        }
    }, [show, firmId, dispatch]);

    const formRef = useRef();

    const defaultValues = {
        name: '',
        model: '',
        serial_number: '',
        firm_id: '',
        operator_id: '',
        description: '',
        status: 'active'
    };

    const [inputs, setInputs] = useState(defaultValues);
    const [errors, setErrors] = useState({});

    const handleSubmit = async (event) => {
        event.preventDefault();
        const data = {
            name: inputs.name,
            model: inputs.model,
            serial_number: inputs.serial_number,
            firm_id: firmId || inputs.firm_id, // Ensure firm_id is always from global active firm
            operator_id: inputs.operator_id || null,
            description: inputs.description,
            status: inputs.status
        };
        
        const params = getParams(true);
        if (firmId) {
            params.firm_id = firmId;
        }
        if (currentPage) {
            params.page = currentPage;
        }
        
        const success = await addMachine(data, params);
        if (success) {
            onClose();
        }
    };

    const handleChange = e => {
        setErrors({});
        e.target.oldValue = e.target.value;
        const newInputs = {...inputs, [e.target.name]: e.target.value};
        setInputs(newInputs);
        
        // If firm is changed, filter employees by that firm
        if (e.target.name === 'firm_id') {
            if (e.target.value) {
                getEmployeeOptionsByFirm(e.target.value);
            } else {
                getEmployeeOptions(); // Load all employees if no firm selected
            }
            // Reset operator selection when firm changes
            setInputs(prevState => ({...prevState, [e.target.name]: e.target.value, operator_id: ''}));
        }
    };

    useEffect(() => {
        if (error && loading === false) {
            const errorsItems = error?.errors;
            let errorObject = {};
            if (error && error?.errors) {
                Object.keys(error?.errors).forEach((item) => errorObject[item] = errorsItems[item][0]);
            }
            setErrors(errorObject);
        }
    }, [error]);

    useEffect(() => {
        if (!show) {
            clearErrors();
            setErrors({});
            setInputs(defaultValues);
        }
    }, [show]);

    const onClose = () => {
        clearErrors();
        setInputs(defaultValues);
        setErrors({});
        handleClose();
    };

    return (
        <Fragment>
            <Modal show={show} onHide={onClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Yangi uskuna qo'shish</Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-3 pb-4 mx-3">
                    <form onSubmit={handleSubmit} ref={formRef}>
                        
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="name">Nomi <span className="text-danger">*</span></label>
                                    <input
                                        name="name"
                                        className={'form-control'}
                                        type="text"
                                        value={inputs?.name || ''}
                                        placeholder={'Uskuna nomini kiriting'}
                                        onChange={handleChange}
                                        onKeyUp={e => e.target.oldValue = e.target.value}
                                    />
                                    <div className="error">
                                        {errors?.name}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="model">Model</label>
                                    <input
                                        name="model"
                                        className={'form-control'}
                                        type="text"
                                        value={inputs?.model || ''}
                                        placeholder={'Model nomini kiriting'}
                                        onChange={handleChange}
                                        onKeyUp={e => e.target.oldValue = e.target.value}
                                    />
                                    <div className="error">
                                        {errors?.model}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="serial_number">Seriya raqami</label>
                                    <input
                                        name="serial_number"
                                        className={'form-control'}
                                        type="text"
                                        value={inputs?.serial_number || ''}
                                        placeholder={'Seriya raqamini kiriting'}
                                        onChange={handleChange}
                                        onKeyUp={e => e.target.oldValue = e.target.value}
                                    />
                                    <div className="error">
                                        {errors?.serial_number}
                                    </div>
                                </div>
                            </div>
                            
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="operator_id">
                                        Operator 
                                        {inputs.firm_id && (
                                            <small className="text-muted"> (tanlangan firmaga tegishli)</small>
                                        )}
                                    </label>
                                    <select name="operator_id"
                                            value={inputs?.operator_id || ''}
                                            id="operator_id"
                                            className="form-control"
                                            onChange={handleChange}
                                            onKeyUp={e => e.target.oldValue = e.target.value}
                                            disabled={!inputs.firm_id}
                                    >
                                        <option value="">
                                            {inputs.firm_id ? 'Operator tanlang' : 'Avval firma tanlang'}
                                        </option>
                                        {employee_options && employee_options.map((item, index) => (
                                            <option key={index} value={item.id}>
                                                {item.first_name} {item.last_name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="error">
                                        {errors?.operator_id}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="status">Holati <span className="text-danger">*</span></label>
                                    <select name="status"
                                            value={inputs?.status || 'active'}
                                            id="status"
                                            className="form-control"
                                            onChange={handleChange}
                                            onKeyUp={e => e.target.oldValue = e.target.value}
                                    >
                                        <option value="active">Faol</option>
                                        <option value="maintenance">Ta'mirlash</option>
                                        <option value="inactive">Nofaol</option>
                                    </select>
                                    <div className="error">
                                        {errors?.status}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="description">Tavsif</label>
                            <textarea
                                name="description"
                                className={'form-control'}
                                rows="3"
                                value={inputs?.description || ''}
                                placeholder={'Uskuna haqida qo\'shimcha ma\'lumot'}
                                onChange={handleChange}
                                onKeyUp={e => e.target.oldValue = e.target.value}
                            />
                            <div className="error">
                                {errors?.description}
                            </div>
                        </div>

                        <div className="d-flex gap-2">
                            <Button className="w-50" variant="secondary" type="button" onClick={onClose}>
                                Bekor qilish
                            </Button>
                            <Button className="w-50" variant="primary" type="submit" disabled={loading}>
                                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
                            </Button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
};

MachineCreateForm.propTypes = {
    addMachine: PropTypes.func.isRequired,
    clearErrors: PropTypes.func.isRequired,
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    machine: state.machine,
});

export default connect(mapStateToProps, {
    addMachine, 
    clearErrors
})(MachineCreateForm); 